import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContentExclusions } from "@/context/ContentExclusionContext";
import client from "@/lib/api";
import {
  Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  House, Sparkle, Compass, Trophy, BookmarkSimple, MagnifyingGlass, UsersThree, ChartBar,
  UserPlus, Gift, Bell, Popcorn, BoxingGlove, PuzzlePiece, CalendarBlank, Tag, Star,
  CalendarCheck, CheckCircle, ClockCounterClockwise, BookOpen, DownloadSimple, Buildings,
  Medal, GearSix, UserCircle, Question, Newspaper, Megaphone, Shuffle, SlidersHorizontal,
  Diamond, FilmSlate, UploadSimple,
} from "@phosphor-icons/react";

// Everything reachable from the app, one keystroke away — the fast lane for
// power users and the safety net for anything that got tucked into a
// collapsed sidebar group. cmdk's built-in filter fuzzy-matches label/keywords.
const GROUPS = [
  {
    heading: "Go to",
    items: [
      { to: "/app", label: "Home", icon: House },
      { to: "/app/bari", label: "BARI — AI Concierge", icon: Sparkle, keywords: "chat recommend ask" },
      { to: "/app/screening-room", label: "Discover", icon: Compass, keywords: "collections browse discover screening room" },
      { to: "/app/best-on", label: "Best On — top picks per service", icon: Trophy, keywords: "netflix hulu streaming" },
      { to: "/app/library", label: "Library", icon: BookmarkSimple, keywords: "watched watchlist ratings" },
      { to: "/app/search", label: "Search titles", icon: MagnifyingGlass },
    ],
  },
  {
    heading: "Social",
    items: [
      { to: "/app/friends", label: "Friends & Circles", icon: UsersThree },
      { to: "/app/screening-room", label: "Discover", icon: ChartBar, keywords: "shared lists feed screening room" },
      { to: "/app/friends-hub?tab=discover", label: "Discover People", icon: UserPlus },
      { to: "/app/invite", label: "Invite friends", icon: Gift },
      { to: "/app/notifications", label: "Notifications", icon: Bell },
    ],
  },
  {
    heading: "Play & Interact",
    items: [
      { to: "/app/watch-party", label: "Watch Party", icon: Popcorn },
      { to: "/app/vibe", label: "Vibe Rooms", icon: Sparkle },
      { to: "/app/duel", label: "Watch Duel", icon: BoxingGlove },
      { to: "/app/trivia", label: "Trivia", icon: PuzzlePiece },
    ],
  },
  {
    heading: "Plan & Track",
    items: [
      { to: "/app/calendar", label: "Release Calendar", icon: CalendarBlank, keywords: "release air date reminders calendar" },
      { to: "/app/library?tab=collections&mode=tags", label: "Tags", icon: Tag, keywords: "collections" },
      { to: "/app/tracker", label: "Star & Franchise Tracker", icon: Star },
      { to: "/app/binge", label: "Binge Planner", icon: CalendarCheck },
      { to: "/app/catch-me-up", label: "Catch Me Up", icon: CheckCircle },
      { to: "/app/rewind", label: "Rewind — year in review", icon: ClockCounterClockwise },
      { to: "/app/library?tab=calendar", label: "Watch Log", icon: BookOpen, keywords: "diary calendar timeline recap" },
    ],
  },
  {
    heading: "More",
    items: [
      { to: "/app/import", label: "Import your data", icon: DownloadSimple, keywords: "letterboxd imdb trakt" },
      { to: "/app/export", label: "Export your data", icon: UploadSimple, keywords: "csv download letterboxd zip email" },
      { to: "/app/household", label: "Household", icon: Buildings },
      { to: "__badges__", label: "Badges", icon: Medal },
      { to: "/app/gems", label: "Hidden Gems", icon: Diamond },
      { to: "/app/news", label: "News", icon: Newspaper },
      { to: "/app/briefing", label: "Briefing", icon: Megaphone },
      { to: "/app/matchmaker", label: "Matchmaker", icon: Shuffle },
      { to: "/app/optimizer", label: "Streaming Optimizer", icon: SlidersHorizontal },
      { to: "/app/profile", label: "Profile", icon: UserCircle },
      { to: "/app/settings", label: "Settings", icon: GearSix },
      { to: "/app/help", label: "Help", icon: Question },
    ],
  },
];

const FLAT_ITEMS = GROUPS.flatMap((g) => g.items);
const RECENT_KEY = "sc_palette_recent";
const MAX_RECENTS = 5;
function loadRecents() {
  try { return (JSON.parse(localStorage.getItem(RECENT_KEY)) || []).slice(0, MAX_RECENTS); } catch { return []; }
}
function pushRecent(entry) {
  try {
    const next = [entry, ...loadRecents().filter((r) => r.to !== entry.to)].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    return next;
  } catch { return loadRecents(); }
}

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { exclusionParams, activeCount } = useContentExclusions();
  const [query, setQuery] = useState("");
  const [titles, setTitles] = useState([]);
  const [searchingTitles, setSearchingTitles] = useState(false);
  const [friends, setFriends] = useState([]);
  const [recents, setRecents] = useState([]);

  // Reset on close so the next open starts fresh instead of a stale query/results.
  useEffect(() => {
    if (!open) { setQuery(""); setTitles([]); return; }
    setRecents(loadRecents());
    // Friend list is small — fetch once per open and filter client-side, same
    // pattern as the app-command groups, so ⌘K can jump straight to a friend's
    // profile without a dedicated search round-trip per keystroke.
    client.get("/friends").then((r) => setFriends(r.data.results || [])).catch(() => setFriends([]));
  }, [open]);

  // Debounced live movie/show search — lets ⌘K jump straight to a title,
  // not just app pages, mirroring the /app/search catalog lookup.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setTitles([]); return; }
    setSearchingTitles(true);
    let cancelled = false;
    const t = setTimeout(() => {
      client.get("/titles/search", { params: { q, ...exclusionParams } })
        .then((r) => { if (!cancelled) setTitles((r.data.results || []).filter((x) => x.media_type !== "person").slice(0, 6)); })
        .catch(() => { if (!cancelled) setTitles([]); })
        .finally(() => { if (!cancelled) setSearchingTitles(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, exclusionParams]);

  const go = (it) => {
    if (it.to === "__badges__" && !user?.user_id) return;
    const to = it.to === "__badges__" ? `/app/badges/${user?.user_id}` : it.to;
    setRecents(pushRecent({ type: "nav", to: it.to, label: it.label }));
    onOpenChange(false);
    navigate(to);
  };

  const goToTitle = (t) => {
    setRecents(pushRecent({ type: "title", to: `/app/title/${t.media_type}/${t.tmdb_id}`, title: t.title, poster_url: t.poster_url, year: t.year }));
    onOpenChange(false);
    navigate(`/app/title/${t.media_type}/${t.tmdb_id}`);
  };

  const goToFriend = (f) => {
    setRecents(pushRecent({ type: "friend", to: `/app/friend/${f.user_id}`, name: f.name, picture: f.picture, is_best_friend: f.is_best_friend }));
    onOpenChange(false);
    navigate(`/app/friend/${f.user_id}`);
  };

  const selectRecent = (r) => {
    if (r.type === "nav") {
      const it = FLAT_ITEMS.find((x) => x.to === r.to) || r;
      go(it);
    } else {
      setRecents(pushRecent(r));
      onOpenChange(false);
      navigate(r.to);
    }
  };

  const clearRecents = () => {
    try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
    setRecents([]);
  };

  const q = query.trim().toLowerCase();
  const friendMatches = q.length >= 1
    ? friends.filter((f) => (f.name || "").toLowerCase().includes(q) || (f.handle || "").toLowerCase().includes(q)).slice(0, 6)
    : [];
  const filteredGroups = q
    ? GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => `${it.label} ${it.keywords || ""}`.toLowerCase().includes(q)) })).filter((g) => g.items.length)
    : GROUPS;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <Command data-testid="command-palette" shouldFilter={false}>
        <CommandInput
          placeholder="Jump to anything, a title, or a friend…"
          data-testid="command-palette-input"
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>No matches. Try a different title, friend, or feature name.</CommandEmpty>

          {!q && recents.length > 0 && (
            <CommandGroup
              heading={
                <span className="flex w-full items-center justify-between pr-1">
                  Recent
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearRecents(); }}
                    data-testid="command-clear-recents"
                    className="rounded-[6px] px-1.5 py-0.5 text-[11px] font-semibold normal-case text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Clear
                  </button>
                </span>
              }
            >
              {recents.map((r) => {
                const navItem = r.type === "nav" ? FLAT_ITEMS.find((x) => x.to === r.to) : null;
                return (
                  <CommandItem
                    key={`recent-${r.to}`}
                    value={r.label || r.title || r.name || r.to}
                    data-testid={`command-recent-${r.to.replace(/\//g, "-")}`}
                    onSelect={() => selectRecent(r)}
                  >
                    {r.type === "title" ? (
                      r.poster_url ? <img src={r.poster_url} alt="" className="h-8 w-6 shrink-0 rounded-[3px] object-cover" /> : <FilmSlate size={16} className="shrink-0" />
                    ) : r.type === "friend" ? (
                      r.picture ? <img src={r.picture} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" /> : <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/20 text-[11px] font-bold text-gold">{r.name?.[0]?.toUpperCase()}</span>
                    ) : navItem ? (
                      <navItem.icon size={16} className="shrink-0" />
                    ) : (
                      <ClockCounterClockwise size={16} className="shrink-0" />
                    )}
                    <span className="truncate">{r.type === "title" ? r.title : r.type === "friend" ? r.name : (navItem?.label || r.label)}</span>
                    {r.type === "title" && r.year && <span className="ml-auto shrink-0 text-xs text-muted-foreground">{r.year}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {friendMatches.length > 0 && (
            <CommandGroup heading="Friends">
              {friendMatches.map((f) => (
                <CommandItem
                  key={f.user_id}
                  value={f.name}
                  data-testid={`command-friend-${f.user_id}`}
                  onSelect={() => goToFriend(f)}
                >
                  {f.picture ? (
                    <img src={f.picture} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/20 text-[11px] font-bold text-gold">{f.name?.[0]?.toUpperCase()}</span>
                  )}
                  <span className="truncate">{f.name}</span>
                  {f.is_best_friend && <span className="ml-auto shrink-0 text-xs text-muted-foreground">Best friend</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {q.length >= 2 && (searchingTitles || titles.length > 0) && (
            <CommandGroup
              heading={
                activeCount > 0 ? (
                  <span className="flex w-full items-center justify-between pr-1">
                    Titles
                    <span className="text-[11px] font-semibold normal-case text-red-300" data-testid="command-exclusions-hint">{activeCount} exclusion{activeCount > 1 ? "s" : ""} active</span>
                  </span>
                ) : "Titles"
              }
            >
              {searchingTitles && titles.length === 0 && (
                <p className="px-2 py-2 text-[13px] text-muted-foreground">Searching the catalog…</p>
              )}
              {titles.map((t) => (
                <CommandItem
                  key={`${t.media_type}-${t.tmdb_id}`}
                  value={t.title}
                  data-testid={`command-title-${t.media_type}-${t.tmdb_id}`}
                  onSelect={() => goToTitle(t)}
                >
                  {t.poster_url ? (
                    <img src={t.poster_url} alt="" className="h-8 w-6 shrink-0 rounded-[3px] object-cover" />
                  ) : (
                    <FilmSlate size={16} className="shrink-0" />
                  )}
                  <span className="truncate">{t.title}</span>
                  {t.year && <span className="ml-auto shrink-0 text-xs text-muted-foreground">{t.year}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {filteredGroups.map((g) => (
            <CommandGroup key={g.heading} heading={g.heading}>
              {g.items.map((it) => (
                <CommandItem
                  key={it.to}
                  value={`${it.label} ${it.keywords || ""}`}
                  data-testid={`command-item-${it.to.replace(/\//g, "-")}`}
                  onSelect={() => go(it)}
                >
                  <it.icon size={16} />
                  <span>{it.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
